import _classCallCheck from "/data/sieberttalas-com/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/data/sieberttalas-com/node_modules/@babel/runtime/helpers/esm/createClass";

/**
 * MIT License
 *
 * Copyright (c) 2017-present, Elasticsearch BV
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 * THE SOFTWARE.
 *
 */
var ApmBase =
/*#__PURE__*/
function () {
  function ApmBase(serviceFactory, disable) {
    _classCallCheck(this, ApmBase);

    this._disable = disable;
    this.serviceFactory = serviceFactory;
    this._initialized = false;
  }

  _createClass(ApmBase, [{
    key: "init",
    value: function init(config) {
      if (this.isEnabled() && !this._initialized) {
        this._initialized = true;
        var configService = this.serviceFactory.getService('ConfigService');
        /**
         * Set Agent version to be sent as part of metadata to the APM Server
         */

        configService.setVersion('4.2.0');
        this.config(config);
        /**
         * Deactive agent when the active config flag is set to false
         */

        var loggingService = this.serviceFactory.getService('LoggingService');

        if (!configService.isActive()) {
          loggingService.info('RUM agent is inactive');
          return this;
        }

        this.serviceFactory.init();
        var errorLogging = this.serviceFactory.getService('ErrorLogging');
        errorLogging.registerGlobalEventListener();
        var performanceMonitoring = this.serviceFactory.getService('PerformanceMonitoring');
        performanceMonitoring.init();

        if (configService.get('sendPageLoadTransaction')) {
          this._sendPageLoadMetrics();
        }
      }

      return this;
    }
  }, {
    key: "_sendPageLoadMetrics",
    value: function _sendPageLoadMetrics() {
      var transactionService = this.serviceFactory.getService('TransactionService');
      var pageLoadTaskId = 'page-load';
      /**
       * Name of the transaction is set in transaction service to
       * avoid duplicate the logic at multiple places
       */

      var tr = transactionService.startTransaction(undefined, 'page-load');

      if (tr) {
        tr.addTask(pageLoadTaskId);
      }

      var sendPageLoadMetrics = function sendPageLoadMetrics() {
        // to make sure PerformanceTiming.loadEventEnd has a value
        setTimeout(function () {
          if (tr) {
            tr.removeTask(pageLoadTaskId);
          }
        });
      };

      if (document.readyState === 'complete') {
        sendPageLoadMetrics();
      } else {
        window.addEventListener('load', sendPageLoadMetrics);
      }
    }
  }, {
    key: "isEnabled",
    value: function isEnabled() {
      return !this._disable;
    }
    /**
     * When the required config keys are invalid, the agent is deactivated with
     * logging error to the console
     *
     * validation error format
     * {
     *  missing: [ 'key1', 'key2']
     *  invalid: [{
     *    key: 'a',
     *    value: 'abcd',
     *    allowed: 'string'
     *  }]
     * }
     */

  }, {
    key: "config",
    value: function config(_config) {
      var configService = this.serviceFactory.getService('ConfigService');

      var _configService$valida = configService.validate(_config),
          missing = _configService$valida.missing,
          invalid = _configService$valida.invalid;

      if (missing.length === 0 && invalid.length === 0) {
        configService.setConfig(_config);
      } else {
        var loggingService = this.serviceFactory.getService('LoggingService');
        var separator = ', ';
        var message = "RUM Agent isn't correctly configured: ";

        if (missing.length > 0) {
          message += 'Missing config - ' + missing.join(separator);

          if (invalid.length > 0) {
            message += separator;
          }
        }

        invalid.forEach(function (_ref, index) {
          var key = _ref.key,
              value = _ref.value,
              allowed = _ref.allowed;
          message += "".concat(key, " \"").concat(value, "\" contains invalid characters! (allowed: ").concat(allowed, ")") + (index !== invalid.length - 1 ? separator : '');
        });
        loggingService.error(message);
        configService.setConfig({
          active: false
        });
      }
    }
  }, {
    key: "setUserContext",
    value: function setUserContext(userContext) {
      var configService = this.serviceFactory.getService('ConfigService');
      configService.setUserContext(userContext);
    }
  }, {
    key: "setCustomContext",
    value: function setCustomContext(customContext) {
      var configService = this.serviceFactory.getService('ConfigService');
      configService.setCustomContext(customContext);
    }
  }, {
    key: "addTags",
    value: function addTags(tags) {
      var loggingService = this.serviceFactory.getService('LoggingService');
      loggingService.warn('addTags deprecated, please use addLabels');
      this.addLabels(tags);
    }
  }, {
    key: "addLabels",
    value: function addLabels(labels) {
      var configService = this.serviceFactory.getService('ConfigService');
      configService.addLabels(labels);
    } // Should call this method before 'load' event on window is fired

  }, {
    key: "setInitialPageLoadName",
    value: function setInitialPageLoadName(name) {
      if (this.isEnabled()) {
        var configService = this.serviceFactory.getService('ConfigService');
        configService.setConfig({
          pageLoadTransactionName: name
        });
      }
    }
  }, {
    key: "startTransaction",
    value: function startTransaction(name, type) {
      if (this.isEnabled()) {
        var transactionService = this.serviceFactory.getService('TransactionService');
        return transactionService.startTransaction(name, type);
      }
    }
  }, {
    key: "startSpan",
    value: function startSpan(name, type) {
      if (this.isEnabled()) {
        var transactionService = this.serviceFactory.getService('TransactionService');
        return transactionService.startSpan(name, type);
      }
    }
  }, {
    key: "getCurrentTransaction",
    value: function getCurrentTransaction() {
      if (this.isEnabled()) {
        var transactionService = this.serviceFactory.getService('TransactionService');
        return transactionService.getCurrentTransaction();
      }
    }
  }, {
    key: "getTransactionService",
    value: function getTransactionService() {
      if (this.isEnabled()) {
        var transactionService = this.serviceFactory.getService('TransactionService');
        return transactionService;
      }
    }
  }, {
    key: "captureError",
    value: function captureError(error) {
      if (this.isEnabled()) {
        var errorLogging = this.serviceFactory.getService('ErrorLogging');
        return errorLogging.logError(error);
      }
    }
  }, {
    key: "addFilter",
    value: function addFilter(fn) {
      var configService = this.serviceFactory.getService('ConfigService');
      configService.addFilter(fn);
    }
  }]);

  return ApmBase;
}();

export default ApmBase;